import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import Loader from "../../components/Loader.tsx";
import { listRoles, roleApiError } from "../../services/role.service.js";
import { notification } from "../../utils/Notifications.tsx";
import { can } from "../../auth/can.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";

const ListRoles = () => {
  useAppSettings();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [roles, setRoles] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [kind, setKind] = React.useState("all");
  const [error, setError] = React.useState<string | null>(null);

  const canCreate = can("roles.create", user);
  const canView = can("roles.view", user);
  const canAssignRole = can("users.assign_role", user);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listRoles({ includeInactive: true });
      setRoles(Array.isArray(response) ? response : []);
    } catch (err) {
      const message = roleApiError(err);
      setError(message);
      notification("Error", message, "danger");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = roles.filter((role) => {
    const haystack = `${role.name} ${role.code ?? ""}`.toLowerCase();
    if (query && !haystack.includes(query.toLowerCase())) {
      return false;
    }
    if (kind === "system" && !role.isSystem) return false;
    if (kind === "custom" && role.isSystem) return false;
    if (kind === "inactive" && role.isActive) return false;
    return true;
  });

  return (
    <Layout loading={loading}>
      <Title baseTitle="Roles y permisos" basePath="/roles" title="Administración de roles" />
      <div className="card border-0 m-4">
        <div className="p-3 d-flex flex-wrap gap-2 align-items-end">
          <div>
            <label className="form-label fs-13px">Buscar</label>
            <input
              className="form-control"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nombre o code"
            />
          </div>
          <div>
            <label className="form-label fs-13px">Filtro</label>
            <select
              className="form-select"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="system">Sistema</option>
              <option value="custom">Personalizado</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>
          {canCreate && (
            <button
              className="btn btn-azul ms-auto"
              type="button"
              onClick={() => navigate("/roles/nuevo")}
            >
              Nuevo rol
            </button>
          )}
          {canAssignRole && (
            <button
              className="btn btn-outline-primary"
              type="button"
              onClick={() => navigate("/roles/usuarios")}
            >
              Asignar rol a usuario
            </button>
          )}
        </div>
        {error && !loading && (
          <div className="alert alert-danger mx-3">{error}</div>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-center text-secondary p-4">No hay roles para mostrar</p>
        )}
        {!loading && filtered.length > 0 && (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Code</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Usuarios</th>
                  <th>Permisos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((role) => (
                  <tr key={role.id}>
                    <td>{role.name}</td>
                    <td>
                      <code>{role.code}</code>
                    </td>
                    <td>
                      {role.isSystem ? (
                        <span className="badge bg-secondary">Sistema</span>
                      ) : (
                        <span className="badge bg-info">Personalizado</span>
                      )}
                    </td>
                    <td>
                      {role.isActive ? (
                        <span className="badge bg-success">Activo</span>
                      ) : (
                        <span className="badge bg-warning text-dark">Inactivo</span>
                      )}
                    </td>
                    <td>{role.assignedUsersCount ?? 0}</td>
                    <td>{role.permissionsCount ?? 0}</td>
                    <td>
                      {canView && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          type="button"
                          onClick={() => navigate(`/roles/${role.id}`)}
                        >
                          Ver
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {loading && <Loader />}
    </Layout>
  );
};

export default ListRoles;
